/**
 * 自定义下拉选择器组件。
 *
 * 该组件是 CRM 前端项目的统一下拉选择控件，替代原生 `<select>` 元素，
 * 提供更丰富的样式控制和交互能力。根据项目约定，所有前端下拉选择必须使用此组件。
 *
 * 核心特性：
 * - 基础选择：通过 `options` 属性传入选项列表，`value` 双向绑定选中值
 * - 本地搜索：`searchable` 为 true 时在选项列表上方显示搜索框，前端过滤
 * - 异步搜索：传入 `asyncSearch` 回调函数，支持远程搜索（带防抖）
 * - 键盘导航：支持 ArrowUp/ArrowDown 导航、Enter 确认、Escape 关闭
 * - 可清空：`clearable` 为 true 时显示清空按钮
 * - 自适应宽度：`fitContent` 为 true 时下拉菜单使用固定宽度（26rem），适合宽内容
 * - 无障碍：触发按钮 role="combobox"，下拉列表 role="listbox"，选项 role="option"
 * - 外部点击关闭：通过 composedPath 检测点击是否在组件外部
 *
 * 事件：选中值变化时派发 `change` 自定义事件，detail 含 { value } 。
 *
 * 导出：`CrmSelect`（自定义元素 `crm-select`）、`SelectOption` 接口
 *
 * 依赖模块：`BaseElement`（基础元素）
 */
import { css, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';

/** 下拉选项配置接口 */
export interface SelectOption {
  /** 选项显示文本 */
  label: string;
  /** 选项值（字符串） */
  value: string;
}

/**
 * 自定义下拉选择器组件。
 *
 * 继承 `BaseElement`，通过 `@customElement('crm-select')` 注册为自定义元素。
 * 支持本地搜索和异步远程搜索两种模式，通过 `searchable` 和 `asyncSearch` 属性区分。
 *
 * 样式采用 Tailwind CSS 类名内联，`static styles` 仅包含宿主元素的 display 和宽度控制。
 */
@customElement('crm-select')
export class CrmSelect extends BaseElement {
  /** 标签文本（显示在触发按钮上方） */
  @property({ type: String }) label = '';
  /** 占位提示文本（未选中时显示） */
  @property({ type: String }) placeholder = '请选择...';
  /** 当前选中的值 */
  @property({ type: String }) value = '';
  /** 选项列表 */
  @property({ type: Array }) options: SelectOption[] = [];
  /** 是否撑满父容器宽度（reflect 到属性，通过 CSS 选择器控制 display） */
  @property({ type: Boolean, reflect: true }) fullWidth = false;
  /** 是否使用固定宽度模式（下拉菜单宽度 26rem，适合宽内容场景） */
  @property({ type: Boolean, reflect: true }) fitContent = false;
  /** 是否禁用 */
  @property({ type: Boolean }) disabled = false;
  /** 是否无边框模式（透明背景，悬停时显示浅色底色） */
  @property({ type: Boolean }) borderless = false;
  /** 是否启用本地搜索（前端过滤选项） */
  @property({ type: Boolean }) searchable = false;
  /** 是否隐藏下拉箭头图标 */
  @property({ type: Boolean }) hideArrow = false;
  /** 是否使用小号尺寸（减小内边距和字号） */
  @property({ type: Boolean }) small = false;
  /** 是否可清空（显示清空按钮，点击重置 value 为空字符串） */
  @property({ type: Boolean }) clearable = false;
  /** 异步搜索回调函数，传入查询字符串返回 Promise<SelectOption[]> */
  @property({ attribute: false })
  asyncSearch?: (query: string) => Promise<SelectOption[]>;
  /** 异步搜索防抖延迟（毫秒），默认 300ms */
  @property({ type: Number })
  asyncSearchDebounce = 300;
  /** 前缀内容（显示在选中值左侧，如图标或文本） */
  @property({ type: String }) prefix = '';

  /** 下拉菜单是否展开 */
  @state()
  private isOpen = false;

  /** 当前键盘焦点选项索引（-1 表示无焦点） */
  @state()
  private focusedIndex = -1;

  /** 搜索框输入值（本地搜索和异步搜索共用） */
  @state()
  private searchQuery = "";

  /** 异步搜索加载中状态 */
  @state()
  private asyncLoading = false;

  /** 异步搜索返回的选项列表 */
  @state()
  private asyncOptions: SelectOption[] = [];

  /** 异步搜索防抖定时器 ID，null 表示无待执行搜索 */
  private _searchTimer: number | null = null;

  /** 异步搜索请求序号，仅允许最后一次查询更新选项和加载状态 */
  private _asyncSearchRequestId = 0;

  static styles = css`
    /* 宿主元素：默认内联块级，相对定位作为下拉菜单的定位上下文 */
    :host { display: inline-block; position: relative; }
    /* fullWidth 模式：块级撑满父容器，触发按钮也撑满 */
    :host([full-width]) { display: block; width: 100%; }
    :host([full-width]) button { width: 100%; }
  `;

  /** 生命周期：挂载时注册全局 mousedown 监听（捕获阶段），用于检测外部点击关闭下拉 */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this.handleOutsideClick, true);
  }

  /**
   * 生命周期：属性变化前的预处理。
   *
   * 当 `fullWidth` 变化时同步更新宿主元素的 inline style，
   * 确保 display 和 width 与属性状态一致。
   *
   * @param changedProps - 变化的属性名到旧值的映射
   */
  protected override willUpdate(changedProps: Map<string, unknown>) {
    super.willUpdate(changedProps);
    if (changedProps.has('fullWidth')) {
      if (this.fullWidth) {
        this.style.display = 'block';
        this.style.width = '100%';
      } else {
        this.style.display = '';
        this.style.width = '';
      }
    }
  }

  /** 生命周期：卸载时移除监听、定时器并使未完成的异步搜索失效。 */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.handleOutsideClick, true);
    if (this._searchTimer !== null) {
      window.clearTimeout(this._searchTimer);
      this._searchTimer = null;
    }
    this._asyncSearchRequestId++;
  }

  /**
   * 外部点击检测处理器（捕获阶段）。
   *
   * 使用 `composedPath()` 而非 `contains()` 检测点击目标是否在组件内部，
   * 因为 Shadow DOM 中的事件 composedPath 会正确穿越 shadow boundary。
   * 点击外部时关闭下拉并重置焦点索引。
   */
  private handleOutsideClick = (e: Event) => {
    const composedPath = (e as MouseEvent).composedPath() as EventTarget[];
    if (this.isOpen && !composedPath.includes(this)) {
      this.isOpen = false;
      this.focusedIndex = -1;
    }
  };

  /**
   * 切换下拉菜单展开/收起。
   *
   * 禁用状态下直接返回。展开时：
   * - 初始化焦点索引为当前选中项（方便键盘导航）
   * - 清空搜索关键词
   * - 异步搜索模式下立即触发一次空查询搜索（加载初始选项）
   * 收起时重置焦点索引。
   */
  private toggleDropdown() {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      // 根据搜索模式选择选项列表，初始化焦点到当前选中项
      const options = this.asyncSearch ? this.asyncOptions : this.options;
      this.focusedIndex = options.findIndex(o => o.value === this.value);
      this.searchQuery = "";
      // 异步搜索模式下，展开时立即加载初始选项
      if (this.asyncSearch) {
        this._doAsyncSearch('', true);
      }
    } else {
      this.focusedIndex = -1;
    }
  }

  /**
   * 选中某个选项。
   *
   * 若选中的值与当前值相同则仅关闭下拉（避免不必要的 change 事件）。
   * 否则更新 value、关闭下拉、重置焦点索引，并派发 `change` 自定义事件。
   *
   * @param option - 被选中的选项对象
   */
  private selectOption(option: SelectOption) {
    if (option.value === this.value) {
      this.isOpen = false;
      this.focusedIndex = -1;
      return;
    }
    this.value = option.value;
    this.isOpen = false;
    this.focusedIndex = -1;
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: option.value },
      bubbles: true,
      composed: true,
    }));
  }

  /**
   * 清空当前选中值。
   *
   * 阻止事件冒泡（防止触发 toggleDropdown），将 value 置为空字符串，
   * 并派发 `change` 事件通知父组件。
   *
   * @param e - 点击事件对象
   */
  private _handleClear(e: Event) {
    e.stopPropagation();
    this.value = '';
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: '' },
      bubbles: true,
      composed: true,
    }));
  }

  /**
   * 执行异步搜索（带防抖）。
   *
   * 清除上一个防抖定时器，根据 immediate 参数决定延迟：
   * - immediate=true：延迟 0ms 立即执行
   * - immediate=false：延迟 asyncSearchDebounce 毫秒（默认 300ms）
   * 执行时设置加载状态，调用 asyncSearch 回调获取结果。
   * 失败时清空异步选项列表。
   *
   * @param query - 搜索关键词
   * @param immediate - 是否立即执行（跳过防抖延迟）
   */
  private _doAsyncSearch(query: string, immediate = false) {
    if (this._searchTimer) clearTimeout(this._searchTimer);
    const requestId = ++this._asyncSearchRequestId;
    const delay = immediate ? 0 : this.asyncSearchDebounce;
    this._searchTimer = window.setTimeout(async () => {
      this._searchTimer = null;
      this.asyncLoading = true;
      this.asyncOptions = [];
      try {
        const options = await this.asyncSearch!(query);
        if (requestId === this._asyncSearchRequestId) {
          this.asyncOptions = options;
        }
      } catch {
        if (requestId === this._asyncSearchRequestId) {
          this.asyncOptions = [];
        }
      } finally {
        if (requestId === this._asyncSearchRequestId) {
          this.asyncLoading = false;
        }
      }
    }, delay);
  }

  /**
   * 键盘事件处理器。
   *
   * 支持以下键盘操作：
   * - Escape：关闭下拉
   * - Enter / Space（未展开时）：展开下拉
   * - ArrowDown（展开时）：焦点下移，不超过列表底部
   * - ArrowUp（展开时）：焦点上移，不小于 0
   * - Enter（展开且有焦点时）：选中当前焦点项
   *
   * @param e - 键盘事件对象
   */
  private handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.isOpen = false;
      this.focusedIndex = -1;
      return;
    }
    if (!this.isOpen && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      this.toggleDropdown();
      return;
    }
    if (this.isOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.focusedIndex = Math.min(this.focusedIndex + 1, this.filteredOptions.length - 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
      } else if (e.key === 'Enter' && this.focusedIndex >= 0) {
        e.preventDefault();
        this.selectOption(this.filteredOptions[this.focusedIndex]);
      }
    }
  }

  /**
   * 计算选中项的显示标签。
   *
   * 在静态选项和异步选项中查找匹配 value 的选项，
   * 找到则返回其 label，否则返回 placeholder。
   */
  private get selectedLabel(): string {
    const opt = this.options.find(o => o.value === this.value)
      || this.asyncOptions.find(o => o.value === this.value);
    return opt ? opt.label : this.placeholder;
  }

  /** 是否已选中值（用于控制样式，如未选中时显示灰色文字） */
  private get hasValue(): boolean {
    return !!this.value;
  }

  /**
   * 获取当前可选项列表（经过搜索过滤）。
   *
   * - 异步搜索模式：直接返回 asyncOptions（已由远程过滤）
   * - 本地搜索模式：若 searchQuery 为空返回全部选项，否则按 label 小写包含过滤
   */
  private get filteredOptions(): SelectOption[] {
    if (this.asyncSearch) return this.asyncOptions;
    if (!this.searchQuery) return this.options;
    const q = this.searchQuery.toLowerCase();
    return this.options.filter(o => o.label.toLowerCase().includes(q));
  }

  /**
   * 主渲染方法。
   *
   * 布局：
   * 1. 可选的标签文本（label 属性存在时显示）
   * 2. 触发按钮（role="combobox"）：前缀 + 选中标签 + 清空按钮 + 下拉箭头
   * 3. 下拉面板（仅 isOpen 时渲染）：
   *    - 搜索框（searchable 或 asyncSearch 启用时）
   *    - 选项列表（ul role="listbox"），支持加载中、空状态、焦点高亮
   *
   * 宽度模式：
   * - fullWidth：触发按钮和下拉菜单都撑满父容器
   * - fitContent：下拉菜单使用固定宽度 26rem
   *
   * @returns Lit 模板结果
   */
  render() {
    const widthClass = this.fullWidth ? 'w-full' : '';
    const triggerWidthClass = this.fitContent ? 'w-auto' : 'w-full';
    const dropdownWidthClass = this.fitContent ? 'w-[26rem]' : '';
    const dropdownPosition = this.fitContent ? 'left: 0;' : 'left: 0; width: 100%;';

    return html`
      <div class="${widthClass}" @keydown=${this.handleKeydown}>
        ${this.label ? html`<div class="mb-1.5 text-sm font-medium text-text">${this.label}</div>` : ''}
        <!-- 触发按钮：role=combobox，展示选中标签/占位符、前缀、清空按钮、下拉箭头 -->
        <button
          type="button"
          role="combobox"
          aria-expanded=${this.isOpen}
          aria-haspopup="listbox"
          class="flex items-center justify-between ${triggerWidthClass} px-2 ${this.small ? 'py-px text-xs' : 'py-1.5 text-sm'} ${this.borderless ? 'border-none bg-transparent hover:bg-bg' : 'border border-border bg-surface'} rounded ${this.borderless ? '' : 'text-text'} focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${!this.hasValue && !this.borderless ? 'text-text-muted' : ''} ${this.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}${!this.disabled && !this.borderless ? ' hover:border-primary/50' : ''}"
          @click=${this.toggleDropdown}
          ?disabled=${this.disabled}
        >
          ${this.prefix ? html`<span class="mr-1 flex-shrink-0">${this.prefix}</span>` : ''}
          <span class="truncate">${this.selectedLabel}</span>
          ${this.clearable && this.hasValue ? html`
          <button type="button" class="flex-shrink-0 ml-1 w-4 h-4 rounded-full hover:bg-border/40 inline-flex items-center justify-center text-text-muted hover:text-text transition-colors" @click=${this._handleClear}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
          ` : ''}
          ${this.hideArrow ? '' : html`
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 flex-shrink-0 ml-2 text-text-muted transition-transform duration-150 ${this.isOpen ? 'rotate-180' : ''}">
            <path d="m6 9 6 6 6-6"/>
          </svg>
          `}
        </button>

        <!-- 下拉面板：仅 isOpen 时渲染，含搜索框和选项列表 -->
        ${this.isOpen ? html`
          <div class="absolute bg-surface border border-border rounded-md shadow-xl overflow-hidden ${dropdownWidthClass}" style="z-index: 9999; ${dropdownPosition} top: calc(100% + 0.5rem);" role="listbox">
            <!-- 搜索框：searchable 或 asyncSearch 启用时显示 -->
            ${this.searchable || this.asyncSearch ? html`
              <div class="px-2 pt-2 pb-1 border-b border-border">
                <input
                  type="text"
                  class="w-full px-2 py-1 text-sm border border-border rounded bg-bg text-text focus:border-primary focus:outline-none"
                  placeholder="输入搜索..."
                  .value=${this.searchQuery}
                  @input=${(e: Event) => {
                    const val = (e.target as HTMLInputElement).value;
                    this.searchQuery = val;
                    this.focusedIndex = -1;
                    if (this.asyncSearch) {
                      this._doAsyncSearch(val);
                    }
                  }}
                  @keydown=${(e: KeyboardEvent) => {
                    if (e.key === 'Escape') {
                      this.isOpen = false;
                      this.focusedIndex = -1;
                    }
                  }}
                  @click=${(e: Event) => e.stopPropagation()}
                />
              </div>
            ` : ''}
            <!-- 选项列表：支持加载中、空状态、键盘焦点高亮 -->
            <ul class="max-h-60 overflow-y-auto py-1">
              ${this.asyncLoading ? html`
                <!-- 异步搜索加载中 -->
                <li class="px-3 py-4 text-sm text-text-muted text-center">搜索中...</li>
              ` : this.asyncSearch && !this.searchQuery && this.filteredOptions.length === 0 ? html`
                <!-- 异步搜索未输入关键词时的引导文案 -->
                <li class="px-3 py-4 text-sm text-text-muted text-center">输入关键词搜索</li>
              ` : this.filteredOptions.map((opt, i) => html`
                <!-- 单个选项：选中项高亮，键盘焦点项浅高亮，mouseenter 同步焦点索引 -->
                <li
                  role="option"
                  aria-selected=${opt.value === this.value}
                  class="px-3 py-2 text-sm cursor-pointer transition-colors ${opt.value === this.value ? 'bg-primary/10 text-primary font-medium' : 'text-text hover:bg-bg'} ${i === this.focusedIndex ? 'bg-primary/5' : ''}"
                  @click=${() => this.selectOption(opt)}
                  @mouseenter=${() => { this.focusedIndex = i; }}
                >
                  ${opt.label}
                </li>
              `)}
              <!-- 无匹配结果时的空状态文案 -->
              ${!this.asyncLoading && this.filteredOptions.length === 0 && (this.asyncSearch ? this.searchQuery : true) ? html`<li class="px-3 py-4 text-sm text-text-muted text-center">无匹配结果</li>` : ''}
            </ul>
          </div>
        ` : ''}
      </div>
    `;
  }
}
